import React from 'react';
import {
  ButtonIcon,
  IconChevronDown,
  IconChevronUp,
  IconX,
} from '@spotify-internal/encore-web';
import { UserState } from './types';
import { Link } from '../../ui';
import { RequireFeatureFlag } from '../../Auth/RequireFeatureFlag';
import {
  CloseSideMenuButton,
  StyledSideMenu,
  SideMenuAction,
  SidemenuSeparator,
  SideMenuLink,
  SideMenuCategory,
  SideMenuCategoryTitle,
  SideMenuCategoryItem,
} from './NavSideMenu.styles';
import { USE_SEARCH } from './constants';
import { isHelpLive } from '../../Help/util';
import { useT } from '@spotify-internal/i18n-core';
import { useRtl } from '../../../../features/src/i18n';
import { getAccountsDomain } from '../../../../features/src/auth';
import { SideMenuLanguageSection } from './Language';
import { NavArrowIcon } from './utils';

const TRACK_CAT = 'Side Nav';

type NavSideMenuProps = {
  userState: UserState;
  onClose(event: any): void;
};

type MenuState = {
  resources?: boolean;
  help?: boolean;
};

export const NavSideMenu: React.FC<NavSideMenuProps> = ({
  userState,
  onClose,
}) => {
  const t = useT();
  const rtl = useRtl();
  const showLogin = userState === 'loggedOut';
  const showLogout = userState === 'claimed' || userState === 'unclaimed';
  const showDashboard = userState === 'claimed';
  const showClaim = userState === 'unclaimed' || userState === 'loggedOut';
  const [menuState, setMenuState] = React.useState<MenuState>({});

  const toggleMenu = (key: keyof MenuState) => {
    setMenuState(cur => ({ ...cur, [key]: !cur[key] }));
  };

  return (
    <StyledSideMenu
      dir={rtl ? 'rtl' : 'ltr'}
      id="nav-sidemenu"
      tabIndex={0}
      role="dialog"
      aria-label={t(
        'FRODOR_NAV_RESPONSIVE_LABEL',
        'Navigation dialog',
        'Label for responsive navigation dialog',
      )}
    >
      <CloseSideMenuButton
        tabIndex={0}
        id="close-sidemenu"
        onClick={onClose}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            onClose(e);
          }
        }}
        aria-label={t(
          'FRODOR_NAV_RESPONSIVE_CLOSE',
          'Close navigation menu',
          'close navigation menu in responsive display',
        )}
        iconOnly={IconX}
        condensedAll
      />

      <ul>
        {showClaim && (
          <li>
            <SideMenuAction>
              <Link
                data-testid="navigation-sidemenu-claim"
                track={[TRACK_CAT, `Get Access (${userState})`]}
                href="/claim"
                prefetch={false}
              >
                {t('FRODOR_7b3ef2', 'Get access', '')}
              </Link>
            </SideMenuAction>
          </li>
        )}
        {showLogout && (
          <li>
            <SideMenuAction>
              <Link
                data-testid="navigation-sidemenu-logout"
                track={[TRACK_CAT, 'Log Out']}
                href={`${getAccountsDomain()}/logout?continue=${encodeURIComponent(
                  window.location.href,
                )}`}
                prefetch={false}
              >
                {t('FRODOR_4253b5', 'Log out', '')}
              </Link>
            </SideMenuAction>
          </li>
        )}
        {showLogin && (
          <li>
            <SideMenuAction>
              <Link
                data-testid="navigation-sidemenu-login"
                track={[TRACK_CAT, 'Log In']}
                href={`${getAccountsDomain()}/login?continue=${encodeURIComponent(
                  window.location.href,
                )}`}
                prefetch={false}
              >
                {t('FRODOR_284ac7', 'Log in', '')}
              </Link>
            </SideMenuAction>
          </li>
        )}
        {showDashboard && (
          <li>
            <SideMenuAction>
              <Link
                track={[TRACK_CAT, 'Dashboard']}
                href="/c"
                prefetch={false}
                data-testid="navigation-sidemenu-dashboard"
              >
                {t('FRODOR_a7d070', 'Dashboard', '')}
              </Link>
            </SideMenuAction>
          </li>
        )}

        <li>
          <SidemenuSeparator />
        </li>

        <li>
          <SideMenuLink>
            <Link
              track={[TRACK_CAT, 'Features']}
              href="/features"
              data-testid="navigation-sidemenu-features"
            >
              {t('FRODOR_3d7a71', 'Features', '')}
            </Link>
          </SideMenuLink>
        </li>

        {isHelpLive && (
          <li>
            <SideMenuLink>
              <Link
                track={[TRACK_CAT, 'Help']}
                href="/help"
                data-testid="navigation-sidemenu-help"
              >
                {t('FRODOR_1d93a2', 'Help', '')}
              </Link>
            </SideMenuLink>
          </li>
        )}

        {!isHelpLive && (
          <li>
            <SideMenuCategory>
              <ButtonIcon onClick={() => toggleMenu('help')}>
                <SideMenuCategoryTitle>
                  {t('FRODOR_1d93a2', 'Help', '')}
                  {menuState.help ? (
                    <IconChevronUp
                      iconSize={16}
                      aria-label={t('FRODOR_d8794a', 'Close help menu', '')}
                    />
                  ) : (
                    <IconChevronDown
                      iconSize={16}
                      aria-label={t('FRODOR_13855f', 'Open help menu', '')}
                    />
                  )}
                </SideMenuCategoryTitle>
              </ButtonIcon>
              {menuState.help && (
                <ul>
                  <li>
                    <SideMenuCategoryItem>
                      <Link
                        track={[TRACK_CAT, 'Guide']}
                        href="/guide"
                        data-testid="navigation-sidemenu-guide"
                      >
                        {t('FRODOR_b61355', 'Guide', '')}
                      </Link>
                    </SideMenuCategoryItem>
                  </li>
                  <li>
                    <SideMenuCategoryItem>
                      <Link
                        track={[TRACK_CAT, 'FAQs']}
                        href="/faq"
                        data-testid="navigation-sidemenu-faq"
                      >
                        FAQ
                      </Link>
                    </SideMenuCategoryItem>
                  </li>
                </ul>
              )}
            </SideMenuCategory>
          </li>
        )}

        <li>
          <SideMenuCategory>
            <ButtonIcon onClick={() => toggleMenu('resources')}>
              <SideMenuCategoryTitle>
                {t('FRODOR_e8bb44', 'Resources', '')}
                {menuState.resources ? (
                  <IconChevronUp
                    iconSize={16}
                    aria-label={t('FRODOR_04818c', 'Close resources menu', '')}
                  />
                ) : (
                  <IconChevronDown
                    iconSize={16}
                    aria-label={t('FRODOR_4203b1', 'Open resources menu', '')}
                  />
                )}
              </SideMenuCategoryTitle>
            </ButtonIcon>
            {menuState.resources && (
              <ul>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      track={[TRACK_CAT, 'Videos']}
                      href="/videos"
                      data-testid="navigation-sidemenu-videos"
                    >
                      {t('FRODOR_9950ea', 'Videos', '')}
                    </Link>
                  </SideMenuCategoryItem>
                </li>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      track={[TRACK_CAT, 'Blog']}
                      href="/blog"
                      data-testid="navigation-sidemenu-blog"
                    >
                      {t('FRODOR_278509', 'News', '')}
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      data-testid="navigation-sidemenu-events"
                      track={[TRACK_CAT, 'Events']}
                      href="/events"
                    >
                      {t('FRODOR_5172ce', 'Events', '')}
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      data-testid="navigation-infocus"
                      track={[TRACK_CAT, 'In Focus']}
                      href="/infocus?utm_campaign=side-nav&utm_medium=owned-product&utm_source=s4a"
                    >
                      {t(
                        'FRODOR_IN_FOCUS',
                        'In Focus',
                        'Tool defining areas artists can focus on',
                      )}{' '}
                      <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      data-testid="navigation-fanstudy"
                      track={[TRACK_CAT, 'Fan Study']}
                      href="/fanstudy?utm_campaign=side-nav&utm_medium=owned-product&utm_source=s4a"
                    >
                      Fan Study <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      data-testid="navigation-madetobefound"
                      track={[TRACK_CAT, 'Made to Be Found']}
                      href="/found?utm_campaign=side-nav&utm_medium=owned-product&utm_source=s4a"
                    >
                      Made to Be Found <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      data-testid="navigation-promocards"
                      track={[TRACK_CAT, 'Promo Cards']}
                      href="/promocards"
                    >
                      {t(
                        'FRODOR_3697de',
                        'Promo Cards',
                        'Promo is short for promotional',
                      )}{' '}
                      <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      data-testid="navigation-loudclear"
                      track={[TRACK_CAT, 'Loud & Clear']}
                      href="/loudandclear?utm_campaign=side-nav&utm_medium=owned-product&utm_source=s4a"
                    >
                      Loud & Clear <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      data-testid="navigation-noteable"
                      track={[TRACK_CAT, 'Noteable']}
                      href="/noteable?utm_campaign=side-nav&utm_medium=owned-product&utm_source=s4a"
                    >
                      Noteable <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>
              </ul>
            )}
          </SideMenuCategory>
        </li>

        <li>
          <SideMenuLink>
            <Link
              track={[TRACK_CAT, 'Providers']}
              href="/providers"
              data-testid="navigation-sidemenu-directory"
              prefetch={false}
            >
              {t('FRODOR_83327b', 'Providers', '')}
            </Link>
          </SideMenuLink>
        </li>

        {USE_SEARCH && (
          <RequireFeatureFlag
            flag="s4a-frodor-test"
            fallback={<></>}
            waiting={<></>}
          >
            <li>
              <SideMenuLink>
                <Link
                  track={[TRACK_CAT, 'Search']}
                  href="/search"
                  data-testid="navigation-sidemenu-search"
                  prefetch={false}
                >
                  {t('FRODOR_6c677e', 'Search', '')}
                </Link>
              </SideMenuLink>
            </li>
          </RequireFeatureFlag>
        )}
      </ul>
      <SideMenuLanguageSection onButtonClick={onClose} />
    </StyledSideMenu>
  );
};
